import { EMPTY_FLOW, type FlowGraph, type FlowNode, type FlowTrigger } from '@cartly/shared';
import {
  Alert,
  Badge,
  Button,
  Card,
  cx,
  PageHeader,
  Spinner,
  useConfirm,
  useToast,
} from '@cartly/ui';
import { ReactFlowProvider } from '@xyflow/react';
import { ArrowLeft, FlaskConical, Pause, Play, Redo2, Rocket, Save, Undo2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, ApiError, errorMessage } from '../../api/client';
import { useLoader } from '../../app/hooks';
import { useActiveSession, useSession } from '../../app/session';
import { FlowCanvas, TRIGGER_NODE_ID } from './FlowCanvas';
import { NodeInspector, TriggerInspector } from './NodeInspector';
import { RunsPanel } from './RunsPanel';
import { TestPanel } from './TestPanel';
import {
  addNode,
  builderIssues,
  defaultNode,
  duplicateNode,
  removeNode,
  replaceNode,
  schemaIssues,
  setLink,
  type NodeType,
  type Slot,
} from './graph';
import { NODE_INFO, NODE_ORDER, summarize } from './labels';
import { templatesForPhone, useWhatsappChoices } from './useWhatsappChoices';

interface FlowDetail {
  id: string;
  name: string;
  status: 'draft' | 'active' | 'paused' | 'archived';
  draft: { version: number; graph: FlowGraph; updatedAt: string } | null;
  published: { version: number; graph: FlowGraph; publishedAt: string | null } | null;
}

const STATUS_TONE = {
  draft: 'neutral',
  active: 'success',
  paused: 'warning',
  archived: 'neutral',
} as const;
const STATUS_LABEL = {
  draft: 'Not published',
  active: 'Live',
  paused: 'Paused',
  archived: 'Archived',
};

const same = (a: FlowGraph, b: FlowGraph) => JSON.stringify(a) === JSON.stringify(b);

function Builder({ flow, reload }: { flow: FlowDetail; reload: () => Promise<void> }) {
  const toast = useToast();
  const confirm = useConfirm();
  const session = useActiveSession();
  const { can } = useSession();
  const tenantId = session.activeTenant?.id;
  const canManage = can('automations:manage');
  const choices = useWhatsappChoices(tenantId, can('channels:read'));

  const saved: FlowGraph = flow.draft?.graph ?? flow.published?.graph ?? EMPTY_FLOW;
  const [graph, setGraphState] = useState<FlowGraph>(saved);
  // Undo/redo: every edit records the previous graph; history lasts for this page visit.
  const graphRef = useRef<FlowGraph>(saved);
  const past = useRef<FlowGraph[]>([]);
  const future = useRef<FlowGraph[]>([]);
  const show = (g: FlowGraph) => {
    graphRef.current = g;
    setGraphState(g);
  };
  const setGraph = (next: FlowGraph | ((g: FlowGraph) => FlowGraph)) => {
    const cur = graphRef.current;
    const value = typeof next === 'function' ? next(cur) : next;
    if (value === cur) return;
    past.current = [...past.current.slice(-49), cur];
    future.current = [];
    show(value);
  };
  const undo = () => {
    const prev = past.current.at(-1);
    if (!prev) return;
    past.current = past.current.slice(0, -1);
    future.current = [...future.current, graphRef.current];
    show(prev);
  };
  const redo = () => {
    const nxt = future.current.at(-1);
    if (!nxt) return;
    future.current = future.current.slice(0, -1);
    past.current = [...past.current, graphRef.current];
    show(nxt);
  };
  const [baseline, setBaseline] = useState<FlowGraph>(saved);
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState<'builder' | 'runs'>('builder');
  const [busy, setBusy] = useState<'save' | 'publish' | 'status' | null>(null);
  const [publishIssues, setPublishIssues] = useState<string[]>([]);
  const [testing, setTesting] = useState(false);

  const dirty = !same(graph, baseline);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || !canManage) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT'))
        return;
      const k = e.key.toLowerCase();
      if (k === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((k === 'z' && e.shiftKey) || k === 'y') {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const issues = useMemo(() => builderIssues(graph), [graph]);
  // Drafts may have structural problems, but the API only stores schema-valid graphs.
  const canSave = useMemo(() => schemaIssues(graph).length === 0, [graph]);
  const issueNodes = useMemo(
    () => new Set(issues.flatMap((i) => (i.nodeId ? [i.nodeId] : []))),
    [issues],
  );
  const phones = choices.data?.phones ?? [];
  const templates = choices.data?.templates ?? [];
  const selectedNode =
    selected && selected !== TRIGGER_NODE_ID
      ? graph.nodes.find((n) => n.id === selected)
      : undefined;
  const hasUnpublished = dirty || flow.draft !== null;

  const add = (type: NodeType) => {
    const phone = phones[0];
    const list = phone ? templatesForPhone(phones, templates, phone.id) : [];
    const t = list[0];
    const node = defaultNode(graph, type, {
      phoneNumberId: phone?.id ?? '0'.repeat(26),
      templateName: t?.name ?? '',
      templateLanguage: t?.language ?? '',
      params: Array.from({ length: t?.bodyParams ?? 0 }, () => ({
        source: 'customer.firstName' as const,
      })),
    });
    setGraph((g) => addNode(g, node, selectedNode?.id ?? null));
    setSelected(node.id);
  };
  const update = (node: FlowNode) => setGraph((g) => replaceNode(g, node));
  const link = (from: string, slot: Slot, to: string | null) => {
    if (from === TRIGGER_NODE_ID) setGraph((g) => ({ ...g, start: to }));
    else setGraph((g) => setLink(g, from, slot, to));
  };
  const remove = (id: string) => {
    setGraph((g) => removeNode(g, id));
    setSelected(null);
  };
  const duplicate = (id: string) => {
    const copy = duplicateNode(graph, id);
    if (!copy) return;
    setGraph(copy.graph);
    setSelected(copy.id);
  };
  const setTrigger = (trigger: FlowTrigger) => setGraph((g) => ({ ...g, trigger }));

  const save = async (): Promise<boolean> => {
    if (!dirty) return true;
    if (!canSave) {
      toast.error('Fix the highlighted steps before saving');
      return false;
    }
    setBusy('save');
    try {
      await api.put(`/flows/${flow.id}/draft`, { graph });
      setBaseline(graph);
      await reload();
      return true;
    } catch (err) {
      toast.error(errorMessage(err));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const publish = async () => {
    setPublishIssues([]);
    if (!(await save())) return;
    const ok = await confirm({
      title: 'Publish this flow',
      message:
        flow.status === 'active'
          ? 'New events will use this version. Runs already in progress finish on the version they started with.'
          : 'The flow goes live: matching events start runs and messages are sent to customers who opted in.',
      confirmLabel: 'Publish',
    });
    if (!ok) return;
    setBusy('publish');
    try {
      await api.post(`/flows/${flow.id}/publish`, {});
      toast.success('Published');
      await reload();
    } catch (err) {
      const raw =
        err instanceof ApiError ? (err.details as { issues?: { message: string }[] }) : null;
      setPublishIssues(raw?.issues?.map((i) => i.message) ?? [errorMessage(err)]);
    } finally {
      setBusy(null);
    }
  };

  const toggle = async () => {
    setBusy('status');
    try {
      await api.post(`/flows/${flow.id}/${flow.status === 'paused' ? 'resume' : 'pause'}`, {});
      await reload();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const nodeTitle = (id: string, type: string) => {
    const n = graph.nodes.find((x) => x.id === id);
    return n
      ? `${NODE_INFO[n.type].title}: ${summarize(n).slice(0, 40)}`
      : (NODE_INFO[type as NodeType]?.title ?? type);
  };

  return (
    <div className="stack-lg">
      <PageHeader
        title={
          <span className="row">
            <Link
              to="/automations"
              className="btn btn--ghost btn--icon btn--sm"
              aria-label="Back to automations"
            >
              <ArrowLeft size={16} />
            </Link>
            {flow.name}
            <Badge tone={STATUS_TONE[flow.status]}>{STATUS_LABEL[flow.status]}</Badge>
            {flow.published && <Badge tone="primary">v{flow.published.version} live</Badge>}
            {dirty ? (
              <Badge tone="warning">Unsaved changes</Badge>
            ) : (
              flow.published && flow.draft && <Badge tone="warning">Unpublished changes</Badge>
            )}
          </span>
        }
        description="Steps run top to bottom for each customer. Click a step to edit it; drag from a dot to connect steps."
        actions={
          canManage ? (
            <>
              {flow.published && (
                <Button
                  variant="secondary"
                  loading={busy === 'status'}
                  icon={flow.status === 'paused' ? <Play size={14} /> : <Pause size={14} />}
                  onClick={() => void toggle()}
                >
                  {flow.status === 'paused' ? 'Resume' : 'Pause'}
                </Button>
              )}
              <Button
                variant="secondary"
                icon={<FlaskConical size={14} />}
                disabled={graph.nodes.length === 0 || issues.length > 0}
                onClick={() => setTesting(true)}
              >
                Test
              </Button>
              <Button
                variant="secondary"
                icon={<Save size={14} />}
                loading={busy === 'save'}
                disabled={!dirty || !canSave}
                onClick={() => void save()}
              >
                Save draft
              </Button>
              <Button
                variant="primary"
                icon={<Rocket size={14} />}
                loading={busy === 'publish'}
                disabled={graph.nodes.length === 0 || issues.length > 0 || !hasUnpublished}
                onClick={() => void publish()}
              >
                Publish
              </Button>
            </>
          ) : null
        }
      />

      <nav className="tabs settings-tabs" aria-label="Flow views">
        {(
          [
            ['builder', 'Builder'],
            ['runs', 'Runs'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={cx('tabs__tab tabs__tab--button', tab === id && 'tabs__tab--active')}
            aria-current={tab === id ? 'page' : undefined}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </nav>

      {tab === 'runs' ? (
        <RunsPanel flowId={flow.id} tenantId={tenantId} />
      ) : (
        <>
          {publishIssues.length > 0 && (
            <Alert tone="critical" title="The flow could not be published">
              <ul className="issue-list">
                {publishIssues.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            </Alert>
          )}
          {canManage && phones.length === 0 && !choices.loading && (
            <Alert tone="info" title="Connect WhatsApp to add message steps">
              Message steps send from a WhatsApp number.{' '}
              <Link to="/whatsapp/templates">Connect an account</Link> and sync templates first.
            </Alert>
          )}
          <div className="builder">
            <Card
              flush
              className="builder__canvas-card"
              title="Flow"
              actions={
                canManage ? (
                  <div className="builder__palette" role="group" aria-label="Add a step">
                    <Button
                      size="sm"
                      variant="secondary"
                      icon={<Undo2 size={14} />}
                      disabled={past.current.length === 0}
                      title="Undo (Ctrl+Z)"
                      aria-label="Undo"
                      onClick={undo}
                    />
                    <Button
                      size="sm"
                      variant="secondary"
                      icon={<Redo2 size={14} />}
                      disabled={future.current.length === 0}
                      title="Redo (Ctrl+Shift+Z)"
                      aria-label="Redo"
                      onClick={redo}
                    />
                    {NODE_ORDER.map((type) => {
                      const info = NODE_INFO[type];
                      const Icon = info.icon;
                      const needsPhone =
                        (type === 'send_whatsapp_template' || type === 'send_whatsapp_text') &&
                        phones.length === 0;
                      const textBlocked =
                        type === 'send_whatsapp_text' &&
                        graph.trigger !== 'whatsapp.message_received';
                      return (
                        <Button
                          key={type}
                          size="sm"
                          variant="secondary"
                          icon={<Icon size={14} />}
                          disabled={needsPhone || textBlocked || graph.nodes.length >= 50}
                          title={
                            textBlocked
                              ? 'Free text needs the trigger “WhatsApp message received”'
                              : info.help
                          }
                          onClick={() => add(type)}
                        >
                          {info.title}
                        </Button>
                      );
                    })}
                  </div>
                ) : null
              }
            >
              <ReactFlowProvider>
                <FlowCanvas
                  graph={graph}
                  selectedId={selected}
                  issueNodeIds={issueNodes}
                  editable={canManage}
                  onSelect={setSelected}
                  onLink={link}
                  onDeleteNode={remove}
                />
              </ReactFlowProvider>
              {graph.nodes.length === 0 && (
                <p className="builder__hint text-muted">
                  Start by choosing a trigger (click “When”), then add steps above.
                </p>
              )}
            </Card>

            <aside className="builder__side">
              <Card title={selected ? 'Edit step' : 'Details'}>
                {selected === TRIGGER_NODE_ID ? (
                  <TriggerInspector graph={graph} editable={canManage} onChange={setTrigger} />
                ) : selectedNode ? (
                  <NodeInspector
                    key={selectedNode.id}
                    node={selectedNode}
                    graph={graph}
                    phones={phones}
                    templates={templates}
                    issues={issues.filter((i) => i.nodeId === selectedNode.id)}
                    editable={canManage}
                    onChange={update}
                    onLink={link}
                    onSetStart={(id) => setGraph((g) => ({ ...g, start: id }))}
                    onDelete={remove}
                    onDuplicate={duplicate}
                  />
                ) : (
                  <p className="text-muted">
                    Select a step to edit it, or click “When” to change what starts the flow.
                  </p>
                )}
              </Card>
              <Card
                title="Problems"
                subtitle={
                  issues.length === 0 ? undefined : 'Fix these before saving or publishing.'
                }
              >
                {issues.length === 0 ? (
                  <p className="text-muted">No problems found.</p>
                ) : (
                  <ul className="issue-list">
                    {issues.map((i, k) => (
                      <li key={k}>
                        {i.nodeId ? (
                          <button
                            type="button"
                            className="link-button"
                            onClick={() => setSelected(i.nodeId)}
                          >
                            {i.nodeId}
                          </button>
                        ) : null}
                        {i.nodeId ? ': ' : ''}
                        {i.message}
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </aside>
          </div>
        </>
      )}

      <TestPanel
        open={testing}
        flowId={flow.id}
        tenantId={tenantId}
        nodeTitle={nodeTitle}
        prepare={save}
        onClose={() => setTesting(false)}
      />
    </div>
  );
}

/** `/automations/:id`: the visual builder, test mode and run log of one flow. */
export function FlowBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const session = useActiveSession();
  const tenantId = session.activeTenant?.id;
  const flow = useLoader(() => api.get<FlowDetail>(`/flows/${id}`), `${tenantId}:${id}`);

  if (flow.loading && !flow.data) return <Spinner size="lg" label="Loading" />;
  if (flow.error || !flow.data) {
    return (
      <div className="stack">
        <Alert tone="critical" title="Could not open this flow">
          {flow.error ?? 'Flow not found'}
        </Alert>
        <div>
          <Button onClick={() => navigate('/automations')}>Back to automations</Button>
        </div>
      </div>
    );
  }
  return <Builder key={flow.data.id} flow={flow.data} reload={flow.reload} />;
}
